import { createContext, type ReactNode, useContext, useState } from "react";

// Debug mode values
export type DebugMode = "on" | "off";

// Debug context interface
interface DebugContextType {
  debugMode: DebugMode;
  isDebugEnabled: boolean;
  setDebugMode: (mode: DebugMode) => void;
  toggleDebugMode: () => void;
}

// Create the context with default values
const DebugContext = createContext<DebugContextType>({
  debugMode: "off",
  isDebugEnabled: false,
  setDebugMode: () => {},
  toggleDebugMode: () => {},
});

// localStorage key for debug mode
const STORAGE_KEY = "debug-mode";

// Props for the provider component
interface DebugProviderProps {
  children: ReactNode;
}

/**
 * Get the initial debug mode from URL params or localStorage
 * URL params take priority and are persisted to localStorage
 */
const getInitialDebugMode = (): DebugMode => {
  if (typeof window === "undefined") return "off";

  // 1. Check URL params first (they override localStorage)
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get("debug");

  if (debugParam === "1") {
    localStorage.setItem(STORAGE_KEY, "on");
    return "on";
  }
  if (debugParam === "0") {
    localStorage.setItem(STORAGE_KEY, "off");
    return "off";
  }

  // 2. Fall back to localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "on" || stored === "off") {
    return stored;
  }

  return "off";
};

/**
 * Debug provider component
 * Provides global debug mode state with localStorage persistence
 */
export const DebugProvider = ({ children }: DebugProviderProps) => {
  const [debugMode, setDebugModeState] =
    useState<DebugMode>(getInitialDebugMode);

  // Update debug mode and store in localStorage
  const setDebugMode = (mode: DebugMode) => {
    setDebugModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  };

  // Toggle between on and off
  const toggleDebugMode = () => {
    setDebugMode(debugMode === "on" ? "off" : "on");
  };

  return (
    <DebugContext
      value={{
        debugMode,
        isDebugEnabled: debugMode === "on",
        setDebugMode,
        toggleDebugMode,
      }}
    >
      {children}
    </DebugContext>
  );
};

/**
 * Custom hook to use the debug context
 */
export const useDebug = (): DebugContextType => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error("useDebug must be used within a DebugProvider");
  }
  return context;
};
